import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    useDisclosure,
    Button,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    ModalFooter,
    Text,
    Box,
    FormControl,
    FormErrorMessage,
    Image,
    ListItem,
    UnorderedList,
    Textarea,
    FormLabel,
    Center,
    Input,
    Tab,
    TabList,
    TabPanel,
    TabPanels,
    Tabs,
    useConst,
    useBoolean,
    AlertDescription,
    CloseButton,
    Alert,
    Collapse
} from '@chakra-ui/react';

import LandService, { LandClass, LandInterface } from '../../../core/land';

export default function LandMapModal(props: {
    data: LandInterface;
    beforeClose?: Function;
}): React.ReactElement {
    const landService = new LandService();

    const { t } = useTranslation();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const {
        isOpen: alertIsOpen,
        onOpen: alertOnOpen,
        onClose: alertOnClose
    } = useDisclosure();

    const [saving, setSaving] = useBoolean(false);

    const onSubmit = async (data: LandInterface) => {
        setSaving.on();
        await landService.save(new LandClass(data as LandInterface));
        props.beforeClose && props.beforeClose(data);
        setSaving.off();
        onClose();
    };

    useEffect(() => {
        alertOnOpen();
    }, [isOpen]);

    return (
        <>
            <Button
                size='sm'
                colorScheme='green'
                variant='outline'
                onClick={onOpen}
            >
                {t('mapModal.createMap', 'Añade un mapa personalizado')}
            </Button>

            <Modal
                isOpen={isOpen}
                onClose={onClose}
                size='xl'
                isCentered
                scrollBehavior='inside'
            >
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>
                        {t('mapModal.title', 'Añade un mapa personalizado')}
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Collapse in={alertIsOpen}>
                            <Alert
                                mb='4'
                                border='1px'
                                borderRadius={3}
                                borderColor='blue.500'
                                bg={'blue.50'}
                                display={'flex'}
                                justifyContent={'space-between'}
                                alignItems='flex-start'
                            >
                                <Box>
                                    <AlertDescription>
                                        {t(
                                            'mapModal.mapTypes',
                                            'Es posible incluir un mapa desde el catastro o un mapa personalizado. A continuación tiene las instrucciones para incluir cada uno de ellos.'
                                        )}
                                    </AlertDescription>
                                </Box>

                                <CloseButton
                                    position='relative'
                                    right={-3}
                                    top={-2}
                                    onClick={alertOnClose}
                                />
                            </Alert>
                        </Collapse>

                        {isOpen && (
                            <Tabs isFitted isLazy>
                                <TabList>
                                    <Tab>
                                        {t('mapModal.carto', 'Catastral')}
                                    </Tab>
                                    <Tab>
                                        {t('mapModal.custom', 'Personalizado')}
                                    </Tab>
                                </TabList>

                                <TabPanels>
                                    <TabPanel>
                                        <CartoMap
                                            data={props.data}
                                            onSubmit={onSubmit}
                                        />
                                    </TabPanel>
                                    <TabPanel>
                                        <CustomMap
                                            data={props.data}
                                            onSubmit={onSubmit}
                                        />
                                    </TabPanel>
                                </TabPanels>
                            </Tabs>
                        )}
                    </ModalBody>

                    <ModalFooter>
                        <Button
                            colorScheme='orange'
                            variant='outline'
                            size='sm'
                            mr={3}
                            onClick={onClose}
                        >
                            {t('form.close')}
                        </Button>
                        <Button
                            colorScheme='green'
                            variant='outline'
                            size='sm'
                            type='submit'
                            // form={formId}
                            form='mapForm'
                            isLoading={saving}
                        >
                            {t('form.save')}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </>
    );
}

function CartoMap(props: {
    data: LandInterface;
    onSubmit: Function;
}): React.ReactElement {
    const { t } = useTranslation();
    const cartoUrl = useConst(
        'https://www1.sedecatastro.gob.es/Cartografia/mapa.aspx?refcat=21025A016000960000LA'
    );
    const [land, setLand] = useState<LandInterface>(props.data);
    const [error, setError] = useState<Record<string, boolean>>({
        isMissing: false,
        isIncorrect: false,
        isInvalid: false
    });

    const isFormValid = (): boolean => {
        const isValid = (!!land.map.data && land.map.data.length > 10) || false;
        setError({
            isMissing: !!land.map.data,
            isIncorrect: !!land.map.data && land.map.data.length > 10,
            isInvalid: !isValid
        });
        return isValid;
    };

    const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!isFormValid()) {
            return;
        }
        props.onSubmit(land);
    };

    return (
        <Box>
            <Box>
                <Box mb='4'>
                    <Text fontWeight={'bold'}>
                        {t('mapModal.description', 'Descripción')}
                    </Text>
                    <Text>
                        {t(
                            'mapModal.carto.description',
                            'Vista catastral de las parcelas usando el visor oficial del catastro.'
                        )}
                    </Text>
                </Box>

                <Box mb='4'>
                    <Text fontWeight={'bold'}>
                        {t('mapModal.instructions', 'Instrucciones')}
                    </Text>
                    <UnorderedList>
                        <ListItem>
                            {t(
                                'mapModal.simple.requisites1',
                                'Añada las referencia catastral de la parcela. En caso de ser más de una, separelas por comas.'
                            )}
                        </ListItem>
                    </UnorderedList>
                </Box>
            </Box>

            <form id='mapForm' onSubmit={onSubmit} noValidate>
                <FormControl isRequired isInvalid={error.isInvalid}>
                    <FormLabel htmlFor='mapUrl' fontWeight={'bold'}>
                        {t(
                            'mapModal.addRefCat',
                            'Añada la referencia catastral de las parcelas separadas por comas'
                        )}
                        :
                    </FormLabel>
                    <Input
                        id='refCat'
                        defaultValue={
                            land.map?.type === 'carto' ? land.map?.data : ''
                        }
                        placeholder={`${t(
                            'global.example',
                            'Ejemplo'
                        )}: 21025A00800245,21025A00800246`}
                        onChange={(e: any) =>
                            setLand({
                                ...land,
                                map: { type: 'carto', data: e.target.value }
                            })
                        }
                    />
                    <FormErrorMessage>
                        {error.isMissing && t('error.required')}
                        {!error.isMissing &&
                            error.isIncorrect &&
                            t(
                                'error.correct',
                                'La URL del mapa no es correcta.'
                            )}
                    </FormErrorMessage>
                </FormControl>
            </form>

            <Box>
                <Text> {t('global.example', 'Ejemplo')}:</Text>
                <iframe
                    id='cartoIframe'
                    key={cartoUrl}
                    src={cartoUrl}
                    width='100%'
                    height='360'
                    title='map'
                    style={{
                        zIndex: 2,
                        position: 'relative'
                    }}
                    onLoad={() => {
                        setTimeout(() => {
                            document.getElementById('cartoIframe')?.click();
                        }, 1000);
                    }}
                />
            </Box>
        </Box>
    );
}

function CustomMap(props: {
    data: LandInterface;
    onSubmit: Function;
}): React.ReactElement {
    const mapPrefix = useConst('https://www.google.com/maps/');
    const mapExample = useConst(
        '<iframe src="https://www.google.com/maps/d/u/0/embed?mid=1nUFhW37GSKhw9OdbJlHaquPf_nb6fqU&ehbc=2E312F" width="640" height="480"></iframe>'
    );
    const customUrl = useConst(
        'https://www.google.com/maps/d/u/0/embed?mid=1PUrWutBoImDf9ExNz2P7XSYhdmHw1MyW&ehbc=2E312F'
    );
    const { t } = useTranslation();

    const [map, setMap] = useState<Record<string, string>>({
        url: '',
        iframe: ''
    });

    const [error, setError] = useState<Record<string, boolean>>({
        isMissing: false,
        isIncorrect: false,
        isInvalid: false
    });

    const isFormValid = (): boolean => {
        const isValid =
            (map.iframe && map.url && map.url.includes(mapPrefix)) || false;
        setError({
            isMissing: !map.iframe,
            isIncorrect: !map.url || !map.url.includes(mapPrefix),
            isInvalid: !isValid
        });
        return isValid;
    };

    const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!isFormValid()) {
            return;
        }
        props.data.map = {
            type: 'custom',
            data: map.url
        };
        props.onSubmit(props.data);
    };

    return (
        <Box>
            <Box>
                <Box mb='4'>
                    <Text fontWeight={'bold'}>
                        {t('mapModal.description', 'Descripción')}
                    </Text>
                    <Text>
                        {t(
                            'mapModal.custom.description',
                            'Mapa personalizado que permite definir lugares, extensiones, y añador capas que definan regiones y leyendas.'
                        )}
                    </Text>
                </Box>

                <Box mb='4'>
                    <Text fontWeight={'bold'}>
                        {t('mapModal.requisites', 'Requisitos')}
                    </Text>
                    <UnorderedList>
                        <ListItem>
                            {t(
                                'mapModal.requisites1',
                                'Se necesita una cuenta de Google, un correo gmail.'
                            )}
                        </ListItem>
                    </UnorderedList>
                </Box>

                <Box>
                    <Text fontWeight={'bold'}>
                        {t('mapModal.instructions', 'Instrucciones')}
                    </Text>
                    <UnorderedList>
                        <ListItem>
                            {t(
                                'mapModal.custom.instructions1',
                                'Para crear un mapa personalizado has de seguir las instrucciones de Google descritas en el siguiente enlace:'
                            )}
                            <a
                                href='https://support.google.com/mymaps/answer/3024454?hl=es'
                                target='_blank'
                                rel='noreferrer'
                            >
                                <Button
                                    size='sm'
                                    mx={'4'}
                                    colorScheme='green'
                                    boxShadow={'none'}
                                    variant='link'
                                >
                                    {t(
                                        'land.googleInstructions',
                                        'Como crear un mapa'
                                    )}
                                </Button>
                            </a>
                        </ListItem>
                        <ListItem>
                            {t(
                                'mapModal.custom.instructions2',
                                'Una vez hayas creado tu mapa, abre el menú "Insertar en  mi sitio".'
                            )}
                            <Center>
                                <Image
                                    src='/images/lands/mapShare.png'
                                    width={'320px'}
                                ></Image>
                            </Center>
                        </ListItem>
                        <ListItem>
                            {t(
                                'mapModal.custom.instructions4',
                                'Ahora sólo te queda copiar el código de tu mapa y pegarlo en el campo de texto.'
                            )}
                            <Center>
                                <Image
                                    src='/images/lands/mapURL.png'
                                    width={'320px'}
                                ></Image>
                            </Center>
                        </ListItem>
                    </UnorderedList>
                </Box>
            </Box>

            <form id='mapForm' onSubmit={onSubmit} noValidate>
                <FormControl
                    // isRequired
                    isInvalid={error.isInvalid}
                >
                    <FormLabel htmlFor='mapUrl' fontWeight={'bold'}>
                        {t(
                            'mapModal.addURL',
                            'Añade el código del mapa que has creado'
                        )}
                        :
                    </FormLabel>
                    <Textarea
                        id='mapUrl'
                        placeholder={`${t(
                            'global.example',
                            'Ejemplo'
                        )}: ${mapExample}`}
                        defaultValue={
                            map.url || props.data.map?.type === 'custom'
                                ? props.data.map?.data
                                : ''
                        }
                        onChange={(e: any) => {
                            let url = String(e.target.value);
                            url = url && url.split('src="')[1];
                            url = url && url.split('"')[0];
                            setMap({
                                iframe: e.target.value,
                                url: url
                            });
                        }}
                    />
                    <FormErrorMessage>
                        {error.isMissing && t('error.required')}
                        {!error.isMissing &&
                            error.isIncorrect &&
                            t(
                                'error.correct',
                                'La URL del mapa no es correcta.'
                            )}
                    </FormErrorMessage>
                </FormControl>
            </form>

            <Box>
                <Text> {t('global.example', 'Ejemplo')}:</Text>
                <iframe
                    key={customUrl}
                    src={customUrl}
                    width='100%'
                    height='360'
                    title='map'
                    style={{
                        zIndex: 2,
                        position: 'relative'
                    }}
                />
            </Box>
        </Box>
    );
}
